import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../shared/ui/button.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { AnalysisActions } from "./analysis-actions.js";
import { action, money, timestamp } from "./analysis-format.js";
import { AnalysisDeleteDialog, ReportDetail } from "./analysis-report.js";
import {
  useAnalysisHistory,
  useAnalysisMonitors,
  useAnalysisMutation,
  useAnalysisReport,
  type AnalysisReport,
} from "./queries.js";

type HistoryDeletion =
  | { kind: "report"; report: AnalysisReport }
  | { kind: "history"; symbol: string; count: number; createdBefore: string };

export function AiAnalysisHistoryPage() {
  const { monitorId = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const cursor = search.get("cursor") ?? "";
  const reportId = search.get("report") ?? "";
  const monitors = useAnalysisMonitors(true);
  const history = useAnalysisHistory(monitorId, cursor);
  const mutation = useAnalysisMutation();
  const [removedReportId, setRemovedReportId] = useState("");
  const report = useAnalysisReport(reportId === removedReportId ? "" : reportId);
  const [historyDeletion, setHistoryDeletion] = useState<HistoryDeletion | null>(null);
  const [notice, setNotice] = useState("");
  const detailRef = useRef<HTMLElement>(null);
  const pending = mutation.isPending;
  const selectedMonitor = monitors.data?.items.find((item) => item.id === monitorId);
  const symbol = selectedMonitor?.symbol ?? history.data?.items[0]?.symbol ?? "Stock";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${symbol} analysis history · TradingApp`;
    return () => {
      document.title = previousTitle;
    };
  }, [symbol]);

  useEffect(() => {
    if (reportId === "") return;
    detailRef.current?.scrollIntoView?.({ block: "start" });
    detailRef.current?.focus({ preventScroll: true });
  }, [reportId]);

  const reportUrl = (id: string) => {
    const next = new URLSearchParams(search);
    next.set("report", id);
    return `?${next.toString()}#analysis-report`;
  };
  const changePage = (nextCursor: string) => {
    setSearch(nextCursor === "" ? {} : { cursor: nextCursor });
    setHistoryDeletion(null);
  };
  const confirmHistoryDeletion = () => {
    if (historyDeletion === null) return;
    const deletion = historyDeletion;
    if (deletion.kind === "history" || reportId === deletion.report.id) {
      // Detach the selected read before the mutation purges its cache. URL navigation
      // can commit later, which would otherwise let the old query refetch a deleted report.
      flushSync(() => setRemovedReportId(reportId));
    }
    mutation.mutate(
      deletion.kind === "report"
        ? { action: "delete-report", id: deletion.report.id }
        : { action: "clear-history", id: monitorId, createdBefore: deletion.createdBefore },
      {
        onError: () => setRemovedReportId(""),
        onSuccess: (result) => {
          setHistoryDeletion(null);
          const next = new URLSearchParams(search);
          next.delete("cursor");
          if (deletion.kind === "history" || reportId === deletion.report.id) next.delete("report");
          setSearch(next, { replace: true });
          const count = "deletedCount" in result ? result.deletedCount : 1;
          setNotice(
            `${count} analysis report${count === 1 ? "" : "s"} deleted. Schedule and budget usage are unchanged.`,
          );
        },
      },
    );
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="Analysis history">
      <Link to="/ai-analysis" className="ai-analysis-back">
        ← AI Analysis monitors
      </Link>
      <div className="page-toolbar">
        <h1>{symbol} analysis history</h1>
        <div className="button-row">
          <RefreshButton queries={[monitors, history, ...(reportId === "" ? [] : [report])]} />
          <Button
            tone="danger"
            disabled={
              pending ||
              history.isError ||
              history.isFetching ||
              (history.data?.deletableCount ?? 0) === 0
            }
            onClick={() => {
              if (history.data === undefined) return;
              mutation.reset();
              setHistoryDeletion({
                kind: "history",
                symbol,
                count: history.data.deletableCount,
                createdBefore: history.data.clearBefore,
              });
            }}
          >
            Clear history for this symbol
          </Button>
        </div>
      </div>
      <p className="section-description">
        Select a report to view its reasoning, chart and evidence below. All times are New York
        time.
      </p>
      {notice === "" ? null : <p role="status">{notice}</p>}
      {historyDeletion === null ? null : (
        <AnalysisDeleteDialog pending={pending} onCancel={() => setHistoryDeletion(null)}>
          <h3 id="history-delete-title">
            {historyDeletion.kind === "report"
              ? "Delete analysis report?"
              : `Clear ${historyDeletion.symbol} analysis history?`}
          </h3>
          <p id="history-delete-description">
            {historyDeletion.kind === "report"
              ? `Delete the ${historyDeletion.report.symbol} report from ${timestamp(historyDeletion.report.createdAt)}?`
              : `Delete ${historyDeletion.count} finished report${historyDeletion.count === 1 ? "" : "s"} for ${historyDeletion.symbol} across all history pages? Reports created or finished after this preview are kept.`}{" "}
            This cannot be undone. The schedule and budget usage will remain unchanged.
          </p>
          {mutation.error === null ? null : <p role="alert">{mutation.error.message}</p>}
          <div className="button-row">
            <Button
              tone="quiet"
              autoFocus
              disabled={pending}
              onClick={() => setHistoryDeletion(null)}
            >
              Cancel
            </Button>
            <Button tone="danger" disabled={pending} onClick={confirmHistoryDeletion}>
              {pending
                ? "Deleting…"
                : historyDeletion.kind === "report"
                  ? "Confirm delete report"
                  : "Confirm clear history"}
            </Button>
          </div>
        </AnalysisDeleteDialog>
      )}
      <div className="content-card section-stack">
        {history.data === undefined ? null : (
          <p className="field-hint">
            {history.data.reportCount} reports · {history.data.deletableCount} finished reports can
            be deleted. Queued and running analyses are retained.
          </p>
        )}
        {history.isPending ? (
          <p role="status">Loading reports…</p>
        ) : history.isError ? (
          <p role="alert">{history.error.message}</p>
        ) : history.data?.items.length === 0 ? (
          <p>
            No reports yet. Use Run Now from the monitor page or wait for the scheduled analysis.
          </p>
        ) : (
          <div
            className="ai-analysis-history-scroll"
            tabIndex={0}
            role="region"
            aria-label="Report history table"
          >
            <table className="ai-analysis-history-table">
              <caption>{symbol} reports · New York time</caption>
              <thead>
                <tr>
                  <th scope="col">Analysis date</th>
                  <th scope="col">Status</th>
                  <th scope="col">Trigger</th>
                  <th scope="col">If not owned</th>
                  <th scope="col">If owned</th>
                  <th scope="col">API cost</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.data?.items.map((item) => (
                  <tr
                    key={item.id}
                    className={item.id === reportId ? "ai-analysis-history-row--selected" : ""}
                  >
                    <th scope="row">
                      <Link
                        to={reportUrl(item.id)}
                        aria-label={`View report from ${timestamp(item.createdAt)}`}
                        aria-current={item.id === reportId ? "true" : undefined}
                      >
                        {timestamp(item.createdAt)}
                      </Link>
                    </th>
                    <td>
                      <span className="status-pill">{item.status.replaceAll("_", " ")}</span>
                    </td>
                    <td>{item.trigger}</td>
                    <td>{action(item.decision?.entryAction)}</td>
                    <td>{action(item.decision?.holdingAction)}</td>
                    <td>{item.costUsd === undefined ? "—" : money(item.costUsd)}</td>
                    <td>
                      <AnalysisActions
                        symbol={item.symbol}
                        label={`Report actions for ${item.symbol} ${timestamp(item.createdAt)}`}
                        groupLabel={`Actions for report ${item.id}`}
                      >
                        <Button
                          tone="danger"
                          disabled={
                            pending || item.status === "queued" || item.status === "running"
                          }
                          onClick={() => {
                            mutation.reset();
                            setHistoryDeletion({ kind: "report", report: item });
                          }}
                        >
                          Delete report
                        </Button>
                      </AnalysisActions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor === "" && history.data?.nextCursor == null ? null : (
          <div className="button-row">
            {cursor === "" ? null : (
              <Button tone="quiet" onClick={() => changePage("")}>
                Newest reports
              </Button>
            )}
            {history.data?.nextCursor == null ? null : (
              <Button tone="quiet" onClick={() => changePage(history.data?.nextCursor ?? "")}>
                Older reports
              </Button>
            )}
          </div>
        )}
      </div>
      <section
        ref={detailRef}
        id="analysis-report"
        className="content-card section-stack ai-analysis-report-detail"
        aria-label="Selected analysis report"
        tabIndex={-1}
      >
        {reportId === "" ? (
          <p className="field-hint">Select a report above to inspect its reasoning and evidence.</p>
        ) : reportId === removedReportId ? (
          <p role="status">{pending ? "Deleting report…" : "This report was deleted."}</p>
        ) : report.isPending ? (
          <p role="status">Loading report…</p>
        ) : report.isError ? (
          <p role="alert">{report.error.message}</p>
        ) : report.data === undefined ? null : report.data.monitorId !== monitorId ? (
          <p role="alert">This report does not belong to this monitor.</p>
        ) : (
          <ReportDetail report={report.data} />
        )}
      </section>
    </section>
  );
}
